<script setup>
  import { inject, computed, onMounted, watch } from 'vue'
  import Footer from '../FooterSection.vue'
  import Breadcrumbs from '../BreadCrumbs.vue'
  import BcsDynamicText from '../BcsDynamicText.vue'
  import TransformLessonVideoPlayer from './TransformLessonVideoPlayer.vue'
  import TransformLessonQuizPlayer from './TransformLessonQuizPlayer.vue'
  import TransformLessonSidebar from './TransformLessonSidebar.vue'
  import BcstSupportingMaterials from './BcstSupportingMaterials.vue'
  import BcstLessonContent from './BcstLessonContent.vue'
  import BcstQuizLockedContent from './BcstQuizLockedContent.vue'
  import BcstLessonBadge from './BcstLessonBadge.vue'
  import TransformOwnerBanner from './TransformOwnerBanner.vue'
  import { useTransformLearningStore } from '../../stores/TransformLearningStore'
  import { useTransformAccessGate } from '../../composables/useTransformAccessGate'
  import { formatDuration } from '../../utils/durationHelpers'
  import lockIconWhite from '@/assets/images/bcst-lock-quiz-badge-white.svg'

  const customNavigation = inject('customNavigation')
  const toggleLoadingWithMessage = inject('toggleLoadingWithMessage', () => {})

  const store = useTransformLearningStore()
  const { isAccessChecked, ensureAccess } = useTransformAccessGate()

  const lessonId = computed(() => customNavigation.value.navigationParams.lessonId)

  // Raw catalog lesson (resolved once the dashboard catalogue is loaded).
  const lesson = computed(() => store.getLessonById(lessonId.value))
  const moduleId = computed(() => lesson.value?.moduleId ?? null)

  // Enriched module detail (status, quiz data, ordering) for the sidebar + neighbours.
  const detailData = computed(() =>
    moduleId.value ? store.categoryDetail(moduleId.value) : null
  )

  const enrichedLesson = computed(
    () => detailData.value?.lessons.find(l => l.id === lessonId.value) ?? null
  )

  const categoryTitle = computed(() => detailData.value?.module?.title ?? '')

  const isVideo = computed(() => lesson.value?.lessonType === 'video')
  const isQuiz = computed(() => lesson.value?.lessonType === 'quiz')

  // Per-type presentation number (Lesson N / Quiz N) + matching total for "Lesson X of Y".
  // Computed in the store so the numbering matches the module detail and sidebar exactly.
  const lessonIndex = computed(() => enrichedLesson.value?.displayNumber ?? null)
  const lessonTotal = computed(() =>
    isQuiz.value ? (detailData.value?.quizTotal ?? null) : (detailData.value?.videoTotal ?? null)
  )
  const isLocked = computed(() => isQuiz.value && enrichedLesson.value?.quiz?.locked === true)
  const requiredLesson = computed(() => {
    const requiredId = enrichedLesson.value?.quiz?.requiredLessonId
    if (!requiredId) return null
    return store.getLessonById(requiredId) ?? null
  })

  // Saved progress for this lesson (seeds the video/quiz tracker).
  const progress = computed(() => store.lessonProgressMap[lessonId.value] ?? null)

  // Quiz-specific seeds from the progress doc.
  const quizAssessmentId = computed(
    () => store.assessmentsByLessonId[lessonId.value]?.id ?? null
  )
  const quizPassThreshold = computed(
    () => store.assessmentsByLessonId[lessonId.value]?.passThresholdPercent ?? 75
  )
  const quizInitialQuestionIndex = computed(
    () => progress.value?.currentQuestionIndex ?? 0
  )
  const quizInitialDraftAnswers = computed(
    () => progress.value?.draftAnswers ?? {}
  )
  const quizInitialScorePercent = computed(
    () => progress.value?.scorePercent ?? null
  )
  const quizInitialCorrectCount = computed(
    () => progress.value?.correctCount ?? null
  )
  const quizInitialTotalCount = computed(
    () => progress.value?.totalCount ?? null
  )

  // Title: "Quiz N: …" for quizzes, "Lesson X of Y: …" for videos.
  const lessonTitle = computed(() => {
    if (isQuiz.value && lessonIndex.value !== null) {
      return `Quiz ${lessonIndex.value}: ${lesson.value?.title ?? ''}`
    }
    if (lessonIndex.value !== null && lessonTotal.value !== null) {
      return `Lesson ${lessonIndex.value} of ${lessonTotal.value}: ${lesson.value?.title ?? ''}`
    }
    return lesson.value?.title ?? ''
  })

  // Total video length label for video lessons (shown as text below the title).
  const videoLengthLabel = computed(() => {
    if (!isVideo.value) return ''
    const duration = lesson.value?.videoDurationSeconds ?? 0
    return formatDuration(duration, { zeroLabel: '0m' })
  })

  // Status badge — mirrors the lesson-card status vocabulary.
  const statusBadge = computed(() => {
    switch (enrichedLesson.value?.status) {
      case 'watched': return { label: 'Complete', variant: 'success' }
      case 'passed': return { label: 'Passed', variant: 'success' }
      case 'failed': return { label: 'Not Passed', variant: 'danger' }
      case 'in_progress': return { label: 'In Progress', variant: 'in-progress' }
      case 'locked': return { label: 'Locked', variant: 'locked' }
      default: return { label: 'Not Started', variant: 'not-started' }
    }
  })

  // All lessons across the course, ordered by module position then lesson position.
  const sortedCourseLessons = computed(() => {
    const sortedModules = [...store.modules].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    const result = []
    for (const mod of sortedModules) {
      const modLessons = [...(store.lessonsByModule[mod.id] ?? [])]
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      result.push(...modLessons)
    }
    return result
  })

  // Previous / next lessons by sequential course order (single index lookup).
  const neighbourLessons = computed(() => {
    const lessons = sortedCourseLessons.value
    const idx = lessons.findIndex(l => l.id === lessonId.value)
    return {
      prev: idx > 0 ? lessons[idx - 1] : null,
      next: idx >= 0 && idx < lessons.length - 1 ? lessons[idx + 1] : null,
    }
  })
  const prevLesson = computed(() => neighbourLessons.value.prev)
  const nextLesson = computed(() => neighbourLessons.value.next)

  // Enrich nextLesson with store data to check if it's a locked quiz
  const nextLessonEnriched = computed(() => {
    const next = nextLesson.value
    if (!next) return null
    
    // Get the enriched lesson data from the store's categoryDetail
    const nextModuleId = next.moduleId
    if (!nextModuleId) return next
    
    const nextModuleDetail = store.categoryDetail(nextModuleId)
    if (!nextModuleDetail) return next
    
    const enriched = nextModuleDetail.lessons.find(l => l.id === next.id)
    return enriched ?? next
  })

  // Check if next lesson is a quiz (when current lesson is a video)
  const isNextLessonQuizFromVideo = computed(() => {
    // Apply special quiz button when current lesson is a video (completed or not)
    const currentIsVideo = isVideo.value
    if (!currentIsVideo) return false
    
    const next = nextLessonEnriched.value
    return next?.lessonType === 'quiz'
  })

  // Check if that quiz is locked
  const isNextLessonLockedQuiz = computed(() => {
    if (!isNextLessonQuizFromVideo.value) return false
    const next = nextLessonEnriched.value
    return next?.quiz?.locked === true
  })

  // Check if that quiz is accessible (not locked)
  const isNextLessonAccessibleQuiz = computed(() => {
    if (!isNextLessonQuizFromVideo.value) return false
    const next = nextLessonEnriched.value
    return next?.quiz?.locked !== true
  })

  // Get the required lesson for the locked quiz
  const nextLessonRequiredLesson = computed(() => {
    if (!isNextLessonLockedQuiz.value) return null
    const requiredId = nextLessonEnriched.value?.quiz?.requiredLessonId
    if (!requiredId) return null
    return store.getLessonById(requiredId) ?? null
  })

  // Label for the locked quiz button
  const nextLessonLockedLabel = computed(() => {
    const next = nextLessonEnriched.value
    if (!next) return ''
    const displayNumber = next.displayNumber ?? ''
    return `Lesson ${displayNumber} Quiz Locked`
  })

  // Label for the accessible quiz button
  const nextLessonAccessibleQuizLabel = computed(() => {
    const next = nextLessonEnriched.value
    if (!next) return ''
    return `Go to Quiz: ${next.title}`
  })

  // Helper text below locked button
  const nextLessonLockedHelper = computed(() => {
    const next = nextLessonEnriched.value
    if (!next) return ''
    const displayNumber = next.displayNumber ?? next.position ?? ''
    return `Complete Lesson ${displayNumber} to unlock the quiz.`
  })

  function goToCategory() {
    if (!moduleId.value) return
    customNavigation.value.navigate({
      newPage: customNavigation.value.routes.transformModuleDetail.name,
      params: { moduleId: moduleId.value, moduleTitle: categoryTitle.value }
    })
  }

  // Navigates to the Results Breakdown in the SAME tab, through the virtual router like
  // the rest of the module (sessionStorage-backed restore handles reloads).
  function openResultsBreakdown() {
    if (!lessonId.value) return
    customNavigation.value.navigate({
      newPage: customNavigation.value.routes.transformQuizResultsBreakdown.name,
      params: {
        lessonId: lessonId.value,
        lessonTitle: lessonTitle.value ?? '',
        moduleId: moduleId.value ?? undefined,
        moduleTitle: categoryTitle.value ?? undefined,
        // No `owner` param: the admin view-as context is module-wide and owned by
        // useTransformOwnerContext — when active, the breakdown reads the target from it;
        // when inactive, these are the authenticated user's own results.
      }
    })
  }

  function goToLesson(target) {
    if (!target?.id) return
    customNavigation.value.navigate({
      newPage: customNavigation.value.routes.transformLessonDetail.name,
      params: {
        lessonId: target.id,
        lessonTitle: target.title,
        moduleId: moduleId.value,
        moduleTitle: categoryTitle.value,
      }
    })
  }

  function goToRequiredLesson() {
    if (!requiredLesson.value?.id) return
    customNavigation.value.navigate({
      newPage: customNavigation.value.routes.transformLessonDetail.name,
      params: {
        lessonId: requiredLesson.value.id,
        lessonTitle: requiredLesson.value.title ?? '',
        moduleId: requiredLesson.value.moduleId ?? moduleId.value,
        moduleTitle: categoryTitle.value,
      }
    })
  }

  function patchBreadcrumbTitles() {
    const crumbs = customNavigation.value.breadCrumbs
    if (crumbs.length >= 2 && categoryTitle.value) {
      crumbs[crumbs.length - 2].title = categoryTitle.value
    }
    if (crumbs.length >= 1 && lesson.value?.title) {
      crumbs[crumbs.length - 1].title = lesson.value.title
    }
  }

  watch(
    () => customNavigation.value.breadCrumbs,
    () => {
      if (!isAccessChecked.value) return
      patchBreadcrumbTitles()
    },
    { deep: true }
  )

  // Loads the quiz/assessment data for the CURRENT lesson's module and refreshes
  // breadcrumbs. Returns false when the lesson can't be resolved (redirects to dashboard).
  async function loadLessonData() {
    // Defensive: unresolved lesson (e.g. lessonId lost on a refresh with cleared
    // storage) → redirect to the dashboard rather than render a broken view.
    if (!lesson.value || !moduleId.value) {
      customNavigation.value.navigate({
        newPage: customNavigation.value.routes.transformLearningDashboard.name
      })
      return false
    }

    // Quiz/assessment data powers the sidebar meta and neighbour ordering. No-op when the
    // module is already cached; loaded here because prev/next can cross into another module.
    await store.loadCategoryDetail(moduleId.value)

    // Patch the last two breadcrumbs to the resolved category + lesson titles
    // (routes carry static 'Category' / 'Lesson' placeholders).
    patchBreadcrumbTitles()
    return true
  }

  onMounted(async () => {
    if (!(await ensureAccess())) return

    toggleLoadingWithMessage(true, null)
    try {
      // Ensure catalogue is loaded (no-op when cached) and refresh the user's progress.
      await store.loadDashboard()
      await store.refreshProgress()
      if (!(await loadLessonData())) return
      isAccessChecked.value = true
    } finally {
      toggleLoadingWithMessage(false, null)
    }

    window.scrollTo({ top: 0, behavior: 'smooth' })
  })

  // Lesson → lesson navigation (prev/next, sidebar) is a same-page transition, so this
  // component is NOT remounted: onMounted does not re-run. When the target lesson lives in
  // a different module its quiz data was never loaded, which left valid quizzes showing
  // "unavailable". Re-run the per-lesson load (and scroll to top) on every lessonId change.
  watch(lessonId, async (id, prevId) => {
    if (!isAccessChecked.value || !id || id === prevId) return
    toggleLoadingWithMessage(true, null)
    try {
      // Refresh progress before loading so the newly-keyed player seeds from the latest
      // resume position / quiz draft (mirrors the onMounted path — otherwise a lesson the
      // user updated in another tab/session would seed from stale progress).
      await store.refreshProgress()
      await loadLessonData()
    } finally {
      toggleLoadingWithMessage(false, null)
    }
    window.scrollTo({ top: 0, behavior: 'smooth' })
  })
</script>

<template>
  <div v-if="isAccessChecked" class="transform-lesson-detail">
    <TransformOwnerBanner />
    <div class="tld-body">
      <!-- ── Main column (keeps the page's left gutter) ── -->
      <div class="tld-main">
        <Breadcrumbs />

        <!-- Back to category -->
        <p class="tld-back-link">
          <span
            class="tld-back-trigger"
            role="button"
            tabindex="0"
            @click="goToCategory"
            @keydown.enter.prevent="goToCategory"
            @keydown.space.prevent="goToCategory">
            &lt; Back to Category: {{ categoryTitle }}
          </span>
        </p>

        <!-- Header -->
        <div class="tld-title-row">
          <h1 class="tld-title">{{ lessonTitle }}</h1>
          <BcstLessonBadge v-if="enrichedLesson" :variant="statusBadge.variant">
            {{ statusBadge.label }}
          </BcstLessonBadge>
        </div>

        <p v-if="isVideo && videoLengthLabel" class="tld-video-length">
          Video Length: {{ videoLengthLabel }}
        </p>

        <p v-if="isQuiz" class="tld-quiz-description">
          <BcsDynamicText text="bcstLearningDashboardQuizDefualtDescription" />
        </p>

          <!-- Content: video player OR quiz placeholder -->
          <div class="tld-content">
            <TransformLessonVideoPlayer
              v-if="isVideo"
              :key="`video-${lessonId}`"
              :vimeoVideoId="lesson.vimeoVideoId"
              :lessonId="lessonId"
              :courseId="lesson.courseId"
              :durationSeconds="lesson.videoDurationSeconds ?? 0"
              :initialPercent="progress?.uniquePercentWatched ?? 0"
              :initialCompleted="progress?.completed === true"
              :initialPositionSeconds="progress?.lastPositionSeconds ?? 0"
              :initialSegments="progress?.watchedSegments ?? []" />

            <BcstQuizLockedContent
              v-else-if="isQuiz && isLocked"
              :quizTitle="lesson?.title ?? ''"
              :requiredLesson="requiredLesson"
              @reviewLesson="goToRequiredLesson" />

            <TransformLessonQuizPlayer
              v-else-if="isQuiz"
              :key="`quiz-${lessonId}`"
              :lessonId="lessonId"
              :courseId="lesson.courseId"
              :assessmentId="quizAssessmentId"
              :passThreshold="quizPassThreshold"
              :initialCurrentQuestionIndex="quizInitialQuestionIndex"
              :initialDraftAnswers="quizInitialDraftAnswers"
              :initialCompleted="progress?.completed === true"
              :initialScorePercent="quizInitialScorePercent"
              :initialCorrectCount="quizInitialCorrectCount"
              :initialTotalCount="quizInitialTotalCount"
              :quizTitle="lesson?.title ?? ''"
              @viewBreakdown="openResultsBreakdown" />
          </div>

          <!-- Supporting materials for video lessons -->
          <BcstSupportingMaterials
            v-if="isVideo && lesson"
            :downloads="lesson.downloads || []"
            :courseId="lesson.courseId"
            :lessonId="lessonId" />

          <!-- Lesson description content for video lessons -->
          <BcstLessonContent
            v-if="isVideo && lesson"
            :bodyHtml="lesson.bodyHtml || ''" />

          <!-- Prev / Next navigation -->
          <div class="tld-nav-row">
            <button
              v-if="prevLesson"
              type="button"
              class="tld-nav-btn"
              @click="goToLesson(prevLesson)">
              <i class="bi bi-chevron-left me-2"></i>
              {{ isQuiz ? 'Go to Lesson' : 'Previous Lesson' }}: {{ prevLesson.title }}
            </button>
            <span v-else></span>
            
            <!-- Next Lesson: Locked Quiz variant -->
            <div v-if="nextLesson && isNextLessonLockedQuiz" class="tld-nav-next-locked">
              <button
                type="button"
                class="tld-nav-btn-locked"
                disabled>
                <img :src="lockIconWhite" alt="Locked" class="tld-nav-btn-locked-icon" />
                {{ nextLessonLockedLabel }}
              </button>
              <p class="tld-nav-locked-helper">{{ nextLessonLockedHelper }}</p>
            </div>
            
            <!-- Next Lesson: Accessible Quiz variant (primary button) -->
            <button
              v-else-if="nextLesson && isNextLessonAccessibleQuiz"
              type="button"
              class="tld-nav-btn-quiz"
              @click="goToLesson(nextLesson)">
              {{ nextLessonAccessibleQuizLabel }}<i class="bi bi-chevron-right"></i>
            </button>
            
            <!-- Next Lesson: Normal variant -->
            <button
              v-else-if="nextLesson"
              type="button"
              class="tld-nav-btn tld-nav-btn--next"
              @click="goToLesson(nextLesson)">
              Next Lesson: {{ nextLesson.title }}
              <i class="bi bi-chevron-right ms-2"></i>
            </button>
          </div>
        </div>

        <!-- ── Sidebar ── -->
        <aside class="tld-sidebar">
          <TransformLessonSidebar
            v-if="detailData"
            :moduleTitle="categoryTitle"
            :lessons="detailData.lessons"
            :total="detailData.total"
            :completedCount="detailData.completedCount"
            :percent="detailData.percent"
            :currentLessonId="lessonId" />
        </aside>
    </div>

    <Footer :showMarginTop="false" />
  </div>
</template>

<style scoped lang="scss">
  .transform-lesson-detail {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .transform-lesson-detail :deep(.breadcrumbs) {
    margin-bottom: 32px !important;
  }

  /* Two-column body: flexible main (keeps the page's left gutter) + fixed 324px
     sidebar flush to the right edge, stretched full height so its panel touches the
     top nav above and the footer below (Figma). The row itself has no side padding
     so the sidebar can reach the viewport's right edge. */
  .tld-body {
    display: flex;
    align-items: stretch;
    flex: 1 1 auto;
    min-height: 0;
  }

  .tld-main {
    flex: 1 1 0;
    min-width: 0;
    /* Left gutter mirrors .bcs-page-padding; right gutter separates from the sidebar. */
    padding: 24px 40px 48px 9%;
  }

  .tld-sidebar {
    flex: 0 0 324px;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  /* Panel fills the stretched column so its background/shadow span top-to-bottom. */
  .tld-sidebar :deep(.tls-panel) {
    flex: 1 1 auto;
    min-height: 0;
  }

  @media (max-width: 991.98px) {
    .tld-body {
      flex-direction: column;
    }
    .tld-main {
      padding: 24px 6% 32px;
    }
    .tld-sidebar {
      flex-basis: auto;
      width: 100%;
    }
  }

  /* ── Header ── */
  .tld-title-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
  }

  .tld-title {
    color: var(--Body-Color, #212529);
    font-family: 'DM Sans', sans-serif;
    font-size: 28px;
    font-style: normal;
    font-weight: 500;
    line-height: 130%; /* 36.4px */
    letter-spacing: -0.56px;
    margin: 0;
  }

  /* Quiz default description below the title */
  .tld-quiz-description {
    width: 850px;
    max-width: 100%;
    color: var(--Gray-700, #495057);
    font-family: 'DM Sans', sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 130%; /* 20.8px */
    letter-spacing: -0.32px;
    margin: 0 0 20px 0;
  }

  /* Video length text below the title */
  .tld-video-length {
    color: var(--Gray-600, #6C757D);
    font-family: 'DM Sans', sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 130%; /* 20.8px */
    letter-spacing: -0.32px;
    margin: 7px 0 30px 0;
  }

  /* ── Content ── */
  .tld-content {
    margin-bottom: 16px;
  }

  /* ── Back link ── */
  .tld-back-link {
    margin-bottom: 10px;
  }

  .tld-back-trigger {
    color: var(--Eggplant---Primary, #5C0CB8);
    font-family: 'DM Sans', sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 130%; /* 20.8px */
    letter-spacing: -0.32px;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  /* ── Prev / Next ── */
  .tld-nav-row {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 48px;
  }

  .tld-nav-btn {
    background-color: #f7f1fd;
    border: 1px solid #f7f1fd;
    color: var(--bs-primary);
    font-size: 14px;
    font-weight: 500;
    letter-spacing: -0.28px;
    border-radius: 3.2px;
    padding: 4px 14px;
    height: 28px;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  /* ── Accessible Quiz Button (Primary Style) ── */
  .tld-nav-btn-quiz {
    display: inline-flex;
    align-items: center;
    width: auto;
    height: 28px;
    padding: 4px 8px;
    gap: 8px;
    background-color: var(--bs-primary);
    border: 1px solid var(--bs-primary);
    border-radius: 3.2px;
    color: var(--White, #FFF);
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 130%; /* 18.2px */
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      background-color: #4a0a96;
      border-color: #4a0a96;
    }

    i {
      flex-shrink: 0;
      margin: 0;
    }
  }

  /* ── Locked Next Lesson ── */
  .tld-nav-next-locked {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
  }

  .tld-nav-btn-locked {
    position: relative;
    display: flex;
    width: 211px;
    height: 28px;
    padding: 4px 8px;
    justify-content: center;
    align-items: center;
    gap: 8px;
    border-radius: 3.2px;
    border: 1px solid var(--Gray-600, #6C757D);
    color: var(--White, #FFF);
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 130%; /* 18.2px */
    cursor: not-allowed;
    isolation: isolate;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 2.2px;
      background: var(--Gray-500, #ADB5BD);
      opacity: 0.65;
      z-index: -1;
    }
  }

  .tld-nav-btn-locked-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  .tld-nav-locked-helper {
    color: var(--Gray-600, #6C757D);
    text-align: right;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 130%; /* 18.2px */
    letter-spacing: -0.28px;
    margin: 0;
  }
</style>
